import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { Home, Building2, Palette, ArrowRight } from 'lucide-react';

const services = [
  {
    title: 'Residential Painting',
    description: 'Our residential painting services are designed to enhance the beauty of your home and reflect your unique style. Our skilled roof painters bring a touch of artistry to every brushstroke.',
    icon: Home,
    image: '/images/site/residential.jpg',
    altText: 'Residential roof painting services Sydney - Beautiful home interior',
  },
  {
    title: 'Commercial Painting',
    description: 'Make a lasting impression on clients and employees alike with our professional commercial painting services. Sydney Roof Painters team delivers top-notch painting solutions.',
    icon: Building2,
    image: '/images/site/commercial.jpg',
    altText: 'Commercial roof painting Sydney - Professional business property painting',
  },
  {
    title: 'Decoration',
    description: 'Bring your space to life with our expert decoration services. Whether you\'re aiming for a modern, chic look or a classic, timeless design, our decorators work closely with you.',
    icon: Palette,
    image: '/images/site/decoration.jpg',
    altText: 'Sydney painters decoration services - Interior and exterior design',
  },
];

const Services = () => {
  const { theme } = useTheme();

  return (
    <section 
      id="services" 
      className={`${theme.layout.sectionSpacing} ${theme.colors.backgroundAlt}`}
      data-testid="services-section"
    >
      <div className={theme.layout.container}>
        {/* Header */}
        <div className="text-center mb-16">
          <span className={`${theme.typography.bodyFont} text-[#ff5218] uppercase tracking-widest text-sm font-semibold`}>
            What We Do
          </span>
          <h2 className={`
            ${theme.typography.headingFont} ${theme.typography.headingWeight}
            ${theme.colors.textPrimary} text-3xl md:text-4xl lg:text-5xl mt-4
            ${theme.typography.headingTracking} uppercase
          `}>
            Our Services
          </h2>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div 
                key={service.title}
                className={`group card-lift overflow-hidden ${theme.components.card} p-0`}
                data-testid={`service-${index}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image}
                    alt={service.altText}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    width="400"
                    height="192"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4 w-12 h-12 flex items-center justify-center bg-[#ff5218] text-white">
                    <Icon className="w-6 h-6" />
                  </div>
                </div>

                <div className="p-6">
                  <h3 className={`
                    ${theme.typography.headingFont} ${theme.colors.textPrimary}
                    text-xl md:text-2xl mb-3 ${theme.typography.headingWeight} uppercase
                  `}>
                    {service.title}
                  </h3>
                  <p className={`${theme.typography.bodyFont} ${theme.colors.textSecondary} text-sm leading-relaxed mb-4`}>
                    {service.description}
                  </p>
                  <button className="flex items-center gap-2 font-semibold group/btn text-[#ff5218] uppercase text-sm tracking-wider">
                    <span>Learn More</span>
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-2 transition-transform" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Services */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {['Roof Washing', 'Roof Recoating', 'Roof Repairs', 'Roof Repainting'].map((item) => (
            <div 
              key={item}
              className="flex items-center gap-3 p-4 bg-white border-2 border-slate-200"
            >
              <ArrowRight className="w-5 h-5 text-[#ff5218]" />
              <span className={`${theme.typography.bodyFont} ${theme.colors.textPrimary} font-medium text-sm`}>
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
